import { FC, useMemo, useRef } from 'react';
import { ArticleFragment } from '../../../generated/types';
import {
  Box,
  Button,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
} from '@chakra-ui/react';
import { ParagraphRenderer } from '../../Paragraph/ParagraphRenderer';
import useTableOfContentLinks from '../../../hooks/useTableOfContentLinks';
import { TableOfContents } from '../../Common/TableOfContents';
import { Image } from '../../Common/Image';
import { Metatags } from '../../Layout/Metatags';
import { StickyArticleButtons } from '../../Common/StickyArticleButtons';
import { AuthorAndDate } from '../../Common/AuthorAndDate';
import { StaticAdSlot } from '../../Common/StaticAdSlot';

interface ArticleProps {
  data: ArticleFragment;
}

export const Article: FC<ArticleProps> = ({ data }) => {
  const tableOfContentLinks = useTableOfContentLinks(data.content);
  const tocRef = useRef<HTMLDivElement | null>(null);

  const renderSidebar =
    data.showTableOfContents || data.sidebarBlocks.length > 0;

  return (
    <>
      <Metatags metatags={data.metatags} />
      <Container maxW="container.main" py={9} pos="relative">
        {/* <StaticAdSlot adKey="leaderboard" /> */}

        <Heading as="h1" color="primaryDark">
          {data.headline}
        </Heading>

        <AuthorAndDate authors={data.authors} date={data.updatedAt} />

        <StickyArticleButtons tocRef={tocRef} links={tableOfContentLinks} />

        <Grid
          templateColumns={{
            base: 'repeat(12, minmax(0, 1fr))',
            md: 'repeat(12, minmax(0, 1fr))',
          }}
          gap={{
            base: 0,
            md: 9,
          }}
        >
          <GridItem
            colSpan={{
              base: 12,
              md: renderSidebar ? 8 : 12,
            }}
          >
            {!data.hideImage && data.teaserImage && (
              <Box py={4}>
                <Image
                  borderRadius="sm"
                  image={data.teaserImage}
                  imageStyle={data.teaserImage.imageStyle}
                />
              </Box>
            )}

            <Box
              display={{
                base: 'block',
                md: 'none',
              }}
              py={6}
              ref={tocRef}
            >
              {data.showTableOfContents && (
                <TableOfContents links={tableOfContentLinks} mobileCutted />
              )}
            </Box>

            <ParagraphRenderer
              paragraphs={data.content}
              wrapperProps={{
                __css: {
                  '&.ParagraphText:not(:first-child) > h2': {
                    mt: 2,
                  },
                  '&.ParagraphLinks': {
                    mb: 6,
                  },
                },
              }}
            />
          </GridItem>
          {renderSidebar && (
            <GridItem
              pt={4}
              colSpan={{
                base: 12,
                md: 4,
              }}
            >
              {data.showTableOfContents && (
                <Box
                  display={{
                    base: 'none',
                    md: 'block',
                  }}
                >
                  <TableOfContents links={tableOfContentLinks} />
                </Box>
              )}

              {data.sidebarBlocks?.map((block, i) => (
                <Box mt={8} layerStyle="shadowBox" key={i}>
                  {block.headline && (
                    <Heading mb={4} as="div" variant="boxTitle">
                      <Box as="span">{block.headline}</Box>
                    </Heading>
                  )}

                  <ParagraphRenderer
                    paragraphs={block.content}
                    wrapperProps={{
                      py: 0,
                    }}
                  />
                </Box>
              ))}
            </GridItem>
          )}
        </Grid>

        <StaticAdSlot adKey="end_of_content" />
      </Container>
    </>
  );
};
